import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ConfigProvider, Spin } from "antd";
import { BASE_TOKENS, COMPONENT_TOKENS, TOAST_OPTIONS } from "@/lib/theme";
import toast, { Toaster, ToastBar } from "react-hot-toast";
import { LoadingContextProvider, LoadingSpin } from "@/components/features";
import { MenuProvider } from "@/context/MenuContext";
import { BreadcrumbProvider } from "@/context/BreadcrumbContext";
import { InstProvider } from "@/context/InstContext";
import { EventProvider } from "@/context/EventContext";
import { HelperMenuProvider } from "@/context/HelperMenuContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { SetCookie } from "./service/CookieService";
import { TOKEN_NAME } from "./constants/token.const";
import { useEffect } from "react";
import { useState } from "react";
import { usePlatform } from "@/hooks/usePlatform";
import { applyNativeViewport } from "@/lib/platform";

// dayjs plugin-уудыг анхны load-д бүгдийг нь нэг газар бүртгэнэ. Урьд нь зарим модул
// дотроо side-effect-ээр plugin extend хийдэг байсан тул code-splitting-ийн дараа
// (lazy chunk-ууд тус тус татагдах үед) `dayjs.tz` undefined гэсэн runtime error
// гардаг байсан.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.utc();

// ⚡ Route-level code splitting — энэхүү lazy-ууд анхны page load-д орохгүй,
// тус тус хэрэглэгчийн зочлох route-ийн дагуу backend-аас on-demand татагдана.
// CRA-ийн webpack нь нэр-түвшинд автоматаар тусдаа chunk үүсгэдэг.
const Login = lazy(() => import("./modules/guest/Login"));
const LeasingRequest = lazy(() => import("./modules/guest/LeasingRequest"));
const ForgotPassword = lazy(() => import("@/modules/guest/ForgotPassword"));
const ResetPassword = lazy(() => import("@/modules/guest/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./modules/guest/PrivacyPolicy"));
const Test = lazy(() => import("./modules/client/Test"));
const Profile = lazy(() => import("@/modules/client/Profile"));
const Dashboard = lazy(() => import("@/modules/gp/Dashboard"));
const TestMonitorPage = lazy(() => import("@/modules/gp/TestMonitorPage"));
const SiRoutes = lazy(() => import("@/modules/si/SiRoutes"));
const AdminRoutes = lazy(() => import("@/modules/ad/AdminRoutes"));
const FlowRoutes = lazy(() => import("./modules/pf/FlowRoutes"));
const RpRoutes = lazy(() => import("./modules/rp/RpRoutes"));
const GlobalAdminRoutes = lazy(() => import("@/routes/global-admin.route"));
const NotFound = lazy(() => import("./modules/guest/NotFound"));

// Suspense fallback — chunk хүлээгдэх агшинд харагдах spinner
const RouteFallback = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
        <Spin size="large" />
    </div>
);

/**
 * Мобайл (native) дээр нэмэгдэх antd token-ууд.
 *
 * Desktop-д зориулсан 32px өндөртэй контрол нь хуруугаар дарахад жижиг — iOS HIG
 * болон Material аль аль нь доод тал нь ~44px хүрэлцэх талбай шаарддаг. Фонтыг ч
 * бага зэрэг томсгож утсан дээр уншихад тохиромжтой болгоно.
 *
 * ⚠ Зөвхөн native апп дотор идэвхжинэ — вэб (browser) дээр огт нөлөөлөхгүй.
 */
const MOBILE_TOKENS = {
    fontSize: 15,
    controlHeight: 44,
    controlHeightSM: 36,
    controlHeightLG: 48,
};

function App() {
    const [isLoad, setIsLoad] = useState(false);
    const params = new URLSearchParams(window.location.search);
    const token = params.get("bearerToken");
    const { isMobile } = usePlatform();

    // Native дээр давхар дарж томруулахыг зогсооно (viewport meta-г runtime-д дарж
    // бичнэ — вэб хувилбарын томруулах эрх хэвээр үлдэнэ).
    useEffect(() => {
        applyNativeViewport();
    }, []);

    useEffect(() => {
        if (token) {
            SetCookie(TOKEN_NAME, token);
            setIsLoad(true);
        }
    }, [token]);

    return (
        <LoadingContextProvider>
            {(isLoad || !token) && <AuthProvider>
                <EventProvider>
                    <InstProvider>
                        <ConfigProvider
                            theme={{
                                hashed: false,
                                token: {
                                    ...BASE_TOKENS,
                                    ...(isMobile ? MOBILE_TOKENS : {}),
                                },
                                components: COMPONENT_TOKENS,
                            }}
                        >
                            <HelperMenuProvider>
                                <MenuProvider>
                                    <BreadcrumbProvider>
                                        <MeRoutes />
                                        <LoadingSpin />
                                        <Toaster
                                            position="top-center"
                                            reverseOrder={false}
                                            gutter={8}
                                            containerClassName=""
                                            // Мобайл дээр мэдэгдэл нь дэлгэцийн дээд ирмэгээс эхэлдэг тул
                                            // Dynamic Island болон AppBar-ын доогуур орж таслагддаг байв.
                                            containerStyle={
                                                isMobile
                                                    ? { top: "calc(env(safe-area-inset-top) + 60px)" }
                                                    : {}
                                            }
                                            toastOptions={TOAST_OPTIONS}
                                        >
                                            {/*
                                              * Хаах (✕) товч. react-hot-toast-д үүнийг дотор нь
                                              * оруулсан боломж байхгүй тул ToastBar-ийн render
                                              * prop-оор өөрсдөө нэмнэ.
                                              *
                                              * `color: "inherit"` — toast бүр өөрийн өнгөтэй
                                              * (амжилт ногоон, алдаа улаан, анхдагч цагаан) бөгөөд
                                              * товч нь өөрөө өнгөө залгамжилдаггүй тул заавал.
                                              *
                                              * `loading` төрлийн toast нь кодоор удирдагддаг тул
                                              * гараар хаах товч харуулахгүй.
                                              */}
                                            {(t) => (
                                                <ToastBar toast={t}>
                                                    {({ icon, message }) => (
                                                        <>
                                                            {icon}
                                                            {message}
                                                            {t.type !== "loading" && (
                                                                <button
                                                                    type="button"
                                                                    aria-label="Хаах"
                                                                    title="Хаах"
                                                                    onClick={() => toast.dismiss(t.id)}
                                                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                                                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; }}
                                                                    style={{
                                                                        alignSelf: "flex-start",
                                                                        flex: "0 0 auto",
                                                                        marginLeft: 6,
                                                                        padding: "0 2px",
                                                                        border: "none",
                                                                        background: "transparent",
                                                                        color: "inherit",
                                                                        opacity: 0.6,
                                                                        fontSize: 15,
                                                                        lineHeight: "20px",
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    ✕
                                                                </button>
                                                            )}
                                                        </>
                                                    )}
                                                </ToastBar>
                                            )}
                                        </Toaster>
                                    </BreadcrumbProvider>
                                </MenuProvider>
                            </HelperMenuProvider>
                        </ConfigProvider>
                    </InstProvider>
                </EventProvider>
            </AuthProvider>}
        </LoadingContextProvider>
    );
}

function MeRoutes() {
    const { isAuth } = useAuth();
    return (
        <Router>
            <Suspense fallback={<RouteFallback />}>
                <Routes>
                    <Route path="/login" element={isAuth() ? <Navigate to="/" /> : <Login />} />
                    <Route path="/leasing-request" element={<LeasingRequest />} />
                    <Route path="/forgotpassword" element={isAuth() ? <Navigate to="/" /> : <ForgotPassword />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/resetPassword/:token" element={isAuth() ? <Navigate to="/" /> : <ResetPassword />} />
                    <Route element={<ProtectedRoute />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/dashboard/monitor" element={<TestMonitorPage />} />
                        <Route path="/admin/*" element={<AdminRoutes />} />
                        <Route path="/pf/*" element={<FlowRoutes />} />
                        <Route path="/si/*" element={<SiRoutes />} />
                        <Route path="/rp/*" element={<RpRoutes />} />
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/test" element={<Test />} />
                        <Route path="/profile" element={<Profile />} />

                        {/* Global Admin pages */}
                        <Route path="/global-admin/*" element={<GlobalAdminRoutes />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
        </Router>
    );
}

export default App;
